"use client";

import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { useState, useEffect, useRef } from "react";

type DropdownItem =
  | { label: string; href: string }
  | { label: string; onClick: () => void };

interface DropdownProps {
  label: string;
  items: DropdownItem[];
}

export default function Dropdown({ label, items }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open || !contentRef.current) return;

    const el = contentRef.current;

    const keepBottomVisible = () => {
      const rect = el.getBoundingClientRect();
      const overflow = rect.bottom - window.innerHeight;
      if (overflow > 0) {
        window.scrollBy({ top: overflow + 100, behavior: "smooth" });
      }
    };

    const raf = requestAnimationFrame(keepBottomVisible);

    let ro: ResizeObserver | null = null;
    if ("ResizeObserver" in window) {
      ro = new ResizeObserver(keepBottomVisible);
      ro.observe(el);
    } else {
      const id = setTimeout(keepBottomVisible, 220);
      return () => clearTimeout(id);
    }

    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
    };
  }, [open]);

  return (
    <div
      className="relative w-full border-t border-gray-300 text-start md:border-t-0 md:border-b"
      ref={dropdownRef}
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex justify-between items-center w-full  px-6 py-3 text-xs font-medium "
        aria-haspopup="true"
        aria-expanded={open}
      >
        {label}
        <Image
          src="/assets/images/arrow_blue.svg"
          alt="arrow"
          width={20}
          height={20}
          className={`ms-2 -me-1 w-2 transition-transform duration-200 ${
            open ? "-rotate-90" : "rotate-90"
          }`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            ref={contentRef}
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-grayCustom overflow-hidden"
          >
            <ul className="px-8">
              {items.map((item, idx) => {
                if ("href" in item) {
                  return (
                    <li key={idx} className="flex flex-col">
                      <a
                        href={item.href}
                        className="block py-2 text-xs text-gray-700"
                        onClick={() => setOpen(false)}
                      >
                        {item.label}
                      </a>
                      {/* <div className="border-b border-gray-300 w-5/6 self-end"></div> */}
                    </li>
                  );
                } else if ("onClick" in item) {
                  return (
                    <li key={idx}>
                      <button
                        type="button"
                        onClick={() => {
                          item.onClick();
                          setOpen(false);
                        }}
                        className="block w-full px-4 py-2 text-start text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {item.label}
                      </button>
                    </li>
                  );
                }
                return null;
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
